import type * as mdast from 'mdast';

import {
    MARK_BOLD,
    MARK_CODE,
    MARK_COLOR,
    MARK_ITALIC,
    MARK_KEYBOARD,
    MARK_STRIKETHROUGH,
    MARK_SUBSCRIPT,
    MARK_SUPERSCRIPT,
} from '@gitbook/doc-core';
import type { Text } from '@gitbook/slate';

import { formatMarkColorStyle, transformTextToNode } from '../utils';
import type { GastToMdastTransformFn } from './types';

/**
 * Transform a text from the GitBook document into mdast.
 */
export const transformText: GastToMdastTransformFn<Text, mdast.PhrasingContent, never> =
    async function transformText(node) {
        return transformTextToNode(node, {
            transformTextBreak: () => {
                return [
                    {
                        type: 'break',
                    },
                ];
            },
            transformMark: (mark, children: mdast.PhrasingContent[]) => {
                switch (mark.type) {
                    case MARK_BOLD:
                        return [
                            {
                                type: 'strong',
                                children,
                            },
                        ];
                    case MARK_ITALIC:
                        return [
                            {
                                type: 'emphasis',
                                children,
                            },
                        ];
                    case MARK_STRIKETHROUGH:
                        return [
                            {
                                type: 'delete',
                                children,
                            },
                        ];
                    case MARK_SUBSCRIPT: {
                        const split = extractWhitespaces(children);
                        return [
                            ...split.before,
                            {
                                type: 'html',
                                value: '<sub>',
                            },
                            ...split.content,
                            {
                                type: 'html',
                                value: '</sub>',
                            },
                            ...split.after,
                        ];
                    }
                    case MARK_SUPERSCRIPT: {
                        const split = extractWhitespaces(children);
                        return [
                            ...split.before,
                            {
                                type: 'html',
                                value: '<sup>',
                            },
                            ...split.content,
                            {
                                type: 'html',
                                value: '</sup>',
                            },
                            ...split.after,
                        ];
                    }
                    case MARK_KEYBOARD: {
                        const split = extractWhitespaces(children);

                        return [
                            ...split.before,
                            {
                                type: 'html',
                                value: '<kbd>',
                            },
                            ...split.content,
                            {
                                type: 'html',
                                value: '</kbd>',
                            },
                            ...split.after,
                        ];
                    }
                    case MARK_COLOR: {
                        const style = formatMarkColorStyle(mark);
                        if (!style) {
                            return children;
                        }
                        const split = extractWhitespaces(children);

                        return [
                            ...split.before,
                            {
                                type: 'html',
                                value: `<mark style="${style}">`,
                            },
                            ...split.content,
                            {
                                type: 'html',
                                value: '</mark>',
                            },
                            ...split.after,
                        ];
                    }
                    case MARK_CODE: {
                        const value = children
                            .map((node) => (node.type === 'text' ? node.value : ''))
                            .join('');
                        return [
                            {
                                type: 'inlineCode',
                                value,
                            },
                        ];
                    }
                    default:
                        return children;
                }
            },
        }) as mdast.PhrasingContent[];
    };

/**
 * When using an HTML node, we need to extract whitespace at tthe begining and end
 * to position them before/after the HTML tags.
 */
function extractWhitespaces(children: mdast.PhrasingContent[]): {
    before: mdast.Text[];
    content: mdast.PhrasingContent[];
    after: mdast.Text[];
} {
    const before: mdast.Text[] = [];
    const content: mdast.PhrasingContent[] = [];
    const after: mdast.Text[] = [];

    const firstChild = children[0];
    if (firstChild && firstChild.type === 'text') {
        // @ts-expect-error
        const [startWhitespaces] = firstChild.value.match(/^\s*/);
        const rest = firstChild.value.slice(startWhitespaces.length);

        if (startWhitespaces.length > 0) {
            before.push({
                type: 'text',
                value: startWhitespaces,
            });

            content.push({
                type: 'text',
                value: rest,
            });
        } else {
            content.push(firstChild);
        }
    } else if (firstChild) {
        content.push(firstChild);
    }

    content.push(...children.slice(1));

    const lastChild = content.pop();
    if (lastChild && lastChild.type === 'text') {
        const [endWhitespaces] = lastChild.value.match(/\s*$/);
        const rest = endWhitespaces.length
            ? lastChild.value.slice(0, -endWhitespaces.length)
            : lastChild.value;

        if (endWhitespaces.length > 0) {
            after.unshift({
                type: 'text',
                value: endWhitespaces,
            });

            content.push({
                type: 'text',
                value: rest,
            });
        } else {
            content.push(lastChild);
        }
    } else if (lastChild) {
        content.push(firstChild);
    }

    return { before, after, content };
}
